import {
  IHouseStepFacilities,
  IWizardHouseState,
  TStepErrors,
  TStepPayload,
  TWizardStepComponentEditable,
  TWizardStepComponentEditableView,
  WIZARD_HOUSE_STEPS_PATHS
} from "../../../interfaces";
import { Form } from "antd";
import { StepViewOnlyValue } from "../../../Student/styles";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  createCheckIsReadonlyStepField,
  getChangedFields,
  getStepErrorsFromResponse
} from "../../../utils";

import { HouseAPI } from "../../../../../API/houseApi";
import {
  HouseOtherItemsAndServicesValues,
  HouseSecurityItemsValues,
  THouseOtherItemsAndServices,
  THouseSecurityItems
} from "../../../../../interfaces/interfaces";
import { WizardButtonSelect } from "../../../components";
import {
  BikeAvailable,
  CarbonMonoxide,
  CleaningService,
  CleaningServiceTwo,
  CoveredBike,
  DoorLock,
  DoubleBed,
  FireExtinguisher,
  FreeParking,
  PaidParking,
  SmokeDetector,
  Tv,
  WashingMachine,
  Wifi
} from "components/Icons/Wizard/FacilitiesAccommodation/AllFacilitiesAccommodation";

const SECURITY_ITEMS_ICONS: {
  [key in THouseSecurityItems]: React.ReactNode;
} = {
  smoke_detector: <SmokeDetector />,
  fire_extinguisher: <FireExtinguisher />,
  carbon_monoxide_detector: <CarbonMonoxide />,
  locked_rent_space: <DoorLock />,
  covered_bicycle_shed: <CoveredBike />
};
const OTHER_ITEMS_AND_SERVICES_ICONS: {
  [key in THouseOtherItemsAndServices]: React.ReactNode;
} = {
  wifi: <Wifi />,
  tv: <Tv />,
  washing_machine: <WashingMachine />,
  free_parking: <FreeParking />,
  paid_parking: <PaidParking />,
  bike_available: <BikeAvailable />,
  shared_space_cleaning: <CleaningService />,
  private_space_cleaning: <CleaningServiceTwo />,
  bedding_and_linen: <DoubleBed />
};

const statePath = WIZARD_HOUSE_STEPS_PATHS.Facilities;
export const WizardStepFacilities: TWizardStepComponentEditable<IWizardHouseState> =
  forwardRef(({ state, readonly, viewonly }, ref) => {
    const [values, setValues] = useState<TStepPayload<IHouseStepFacilities>>(
      state[statePath] ?? {}
    );
    const [errors, setErrors] = useState<TStepErrors<typeof values>>(null);

    useImperativeHandle(ref, () => ({
      save: () => {
        setErrors(null);
        return HouseAPI.wizardSaveFacilities(
          state.house_id!,
          state.wizard_type!,
          values
        )
          .then((result) => {
            return {
              ...state,
              [statePath]: result
            };
          })
          .catch((e) => {
            setErrors(getStepErrorsFromResponse(e));
            return Promise.reject(e);
          });
      },
      savePartially: () => {
        setErrors(null);
        return HouseAPI.wizardSaveFacilitiesPartially(
          state.house_id!,
          state.wizard_type!,
          getChangedFields(state[statePath], values)
        )
          .then((result) => {
            return {
              ...state,
              [statePath]: result
            };
          })
          .catch((e) => {
            setErrors(getStepErrorsFromResponse(e));
            return Promise.reject(e);
          });
      }
    }));

    return (
      <WizardStepFacilitiesView
        values={values}
        setValues={setValues}
        errors={errors}
        readonly={readonly}
        viewonly={viewonly}
      />
    );
  });

export const WizardStepFacilitiesView: TWizardStepComponentEditableView<IHouseStepFacilities> =
  React.memo(
    ({ errors, values, setValues, viewonly, readonly, enabledFields }) => {
      const { t } = useTranslation("house");

      const securityItemsOptions = HouseSecurityItemsValues.map((c) => ({
        value: c,
        label: t(`wizard.steps.Facilities.form.security_items.options.${c}`),
        icon: SECURITY_ITEMS_ICONS[c]
      }));
      const otherItemsAndServicesOptions = HouseOtherItemsAndServicesValues.map(
        (c) => ({
          value: c,
          label: t(
            `wizard.steps.Facilities.form.other_items_and_services.options.${c}`
          ),
          icon: OTHER_ITEMS_AND_SERVICES_ICONS[c]
        })
      );

      const isReadonly = createCheckIsReadonlyStepField(
        readonly,
        enabledFields
      );

      return (
        <Form layout="vertical">
          <Form.Item
            label={t(`wizard.steps.Facilities.form.security_items.title`)}
            validateStatus={errors?.security_items ? "error" : ""}
            help={errors?.security_items}
          >
            {viewonly ? (
              <StepViewOnlyValue>
                {securityItemsOptions
                  .filter((c) => values.security_items?.includes(c.value))
                  ?.map((o) => o.label)
                  .join(", ") ?? ""}
              </StepViewOnlyValue>
            ) : (
              <WizardButtonSelect
                disabled={isReadonly("security_items")}
                value={values.security_items ?? undefined}
                options={securityItemsOptions}
                gridTemplate="233px 335px 1fr"
                onChange={(newValue) =>
                  setValues({
                    ...values,
                    security_items: newValue
                  })
                }
              />
            )}
          </Form.Item>
          <Form.Item
            label={t(
              `wizard.steps.Facilities.form.other_items_and_services.title`
            )}
            validateStatus={errors?.other_items_and_services ? "error" : ""}
            help={errors?.other_items_and_services}
          >
            {viewonly ? (
              <StepViewOnlyValue>
                {otherItemsAndServicesOptions
                  .filter((c) =>
                    values.other_items_and_services?.includes(c.value)
                  )
                  ?.map((o) => o.label)
                  .join(", ") ?? ""}
              </StepViewOnlyValue>
            ) : (
              <WizardButtonSelect
                disabled={isReadonly("other_items_and_services")}
                value={values.other_items_and_services ?? undefined}
                options={otherItemsAndServicesOptions}
                gridTemplate="233px 335px 1fr"
                onChange={(newValue) =>
                  setValues({
                    ...values,
                    other_items_and_services: newValue
                  })
                }
              />
            )}
          </Form.Item>
        </Form>
      );
    }
  );
